import CheckoutRepository from './CheckoutRepository.js'

/**
 * @interface
 */
export default class HTTPCheckoutRepository extends CheckoutRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Object} deps.authenticationFetcher
   * @param {Object} deps.paymentMethodsCheckoutRawDataToPaymentMethodsCheckoutVOMapper
   * @param {Object} deps.purchaseCheckoutRawDataToPurchaseCheckoutEntityMapper
   * @param {Object} deps.getPaymentMethodsCheckoutError
   * @param {Object} deps.purchaseCheckoutError
   * @param {Object} deps.purchaseDetailsCheckoutError
   */
  constructor({
    config,
    authenticationFetcher,
    paymentMethodsCheckoutRawDataToPaymentMethodsCheckoutVOMapper,
    purchaseCheckoutRawDataToPurchaseCheckoutEntityMapper,
    orderRawDataToOrderEntityMapper,
    getPaymentMethodsCheckoutError,
    purchaseCheckoutError,
    purchaseDetailsCheckoutError
  }) {
    super()
    this._config = config
    this._authenticationFetcher = authenticationFetcher
    this._paymentMethodsCheckoutRawDataToPaymentMethodsCheckoutVOMapper =
      paymentMethodsCheckoutRawDataToPaymentMethodsCheckoutVOMapper
    this._purchaseCheckoutRawDataToPurchaseCheckoutEntityMapper = purchaseCheckoutRawDataToPurchaseCheckoutEntityMapper
    this._orderRawDataToOrderEntityMapper = orderRawDataToOrderEntityMapper
    this._getPaymentMethodsCheckoutError = getPaymentMethodsCheckoutError
    this._purchaseCheckoutError = purchaseCheckoutError
    this._purchaseDetailsCheckoutError = purchaseDetailsCheckoutError
  }

  /**
   * @method
   * @return {Object}
   */
  _getCustomHeaders({variant}) {
    if (variant) return {'X-Adevinta-MT-Variant': variant}
  }

  /**
   * @method
   * @return {Promise}
   */
  async getPaymentMethods({orderId, testCode}) {
    try {
      const {data: paymentMethods} = await this._authenticationFetcher.get(
        `${this._config.get('API_URL')}/payment-gateways/adyen/payment-methods/`,
        {
          params: {
            orderId,
            testCode
          }
        }
      )
      const paymentMethodsVO = this._paymentMethodsCheckoutRawDataToPaymentMethodsCheckoutVOMapper.map(paymentMethods)
      return paymentMethodsVO
    } catch (error) {
      throw this._getPaymentMethodsCheckoutError(
        '[HTTPCheckoutRepository#getPaymentMethods] Get PaymentMethodsCheckout error'
      )
    }
  }

  /**
   * @method
   * @return {Promise}
   */
  async createOrder({userId, adId, products, testCode, storeId, variant, adPrice}) {
    const headers = this._getCustomHeaders({variant})
    try {
      const {data: order} = await this._authenticationFetcher.post(
        `${this._config.get('API_URL')}/v2/orders/`,
        {
          userId,
          adId,
          products,
          testCode,
          storeId,
          adPrice
        },
        {headers}
      )
      const orderEntity = this._orderRawDataToOrderEntityMapper.map(order)
      return orderEntity
    } catch (error) {
      throw this._getPaymentMethodsCheckoutError(
        '[HTTPProductsRepository#getPaymentMethodsCheckout] Get PaymentMethodsCheckout error'
      )
    }
  }

  /**
   * @method
   * @return {Promise}
   */
  async purchase({paymentInformation, orderId, returnUrl, userId}) {
    try {
      const {data: purchase} = await this._authenticationFetcher.post(
        `${this._config.get('API_URL')}/payment-gateways/adyen/payments/`,
        {
          paymentInformation,
          orderId,
          returnUrl,
          userId
        }
      )
      const purchaseEntity = this._purchaseCheckoutRawDataToPurchaseCheckoutEntityMapper.map(purchase)
      return purchaseEntity
    } catch (error) {
      throw this._purchaseCheckoutError('[HTTPPurchaseCheckoutRepository#purchaseCheckout] POST PurchaseCheckout error')
    }
  }

  /**
   * @method
   * @return {Promise}
   */
  async getPurchaseDetails({data, paymentId, userId}) {
    try {
      const {data: purchaseDetails} = await this._authenticationFetcher.put(
        `${this._config.get('API_URL')}/payment-gateways/adyen/payments/${paymentId}`,
        {...data, userId}
      )
      const purchaseEntity = this._purchaseCheckoutRawDataToPurchaseCheckoutEntityMapper.map(purchaseDetails)
      return purchaseEntity
    } catch (error) {
      throw this._purchaseDetailsCheckoutError(
        '[HTTPPurchaseCheckoutRepository#purchaseDetailsCheckout] PUT PurchaseDetailsCheckout error'
      )
    }
  }
}
